import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, useMediaQuery } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { MotionConfig } from 'framer-motion';
import { createAppTheme, Mode } from '../styles/theme';
import { useLanguage } from './LanguageContext';

type FontSize = 'small' | 'medium' | 'large';

export interface AccessibilitySettings {
  highContrast: boolean;
  reduceAnimations: boolean;
  textToSpeech: boolean;
  fontSize: FontSize;
}

interface ThemeContextType {
  mode: Mode;
  toggleThemeMode: () => void;
  setMode: (mode: Mode) => void;
  accessibility: AccessibilitySettings;
  updateAccessibility: (settings: Partial<AccessibilitySettings>) => void;
  /** true when animations should be kept minimal (OS preference or user setting) */
  reduceMotion: boolean;
}

const defaultAccessibility: AccessibilitySettings = {
  highContrast: false,
  reduceAnimations: false,
  textToSpeech: false,
  fontSize: 'medium',
};

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleThemeMode: () => {},
  setMode: () => {},
  accessibility: defaultAccessibility,
  updateAccessibility: () => {},
  reduceMotion: false,
});

export const useAppTheme = () => useContext(ThemeContext);

const rtlCache = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'mui' });

const readMode = (): Mode => {
  try {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {}
  return 'light';
};

const readAccessibility = (): AccessibilitySettings => {
  try {
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) return { ...defaultAccessibility, ...JSON.parse(saved) };
  } catch {}
  return defaultAccessibility;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isRtl } = useLanguage();
  const [mode, setMode] = useState<Mode>(readMode);
  const [accessibility, setAccessibility] = useState<AccessibilitySettings>(readAccessibility);
  const osReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const reduceMotion = osReducedMotion || accessibility.reduceAnimations;

  useEffect(() => {
    try { localStorage.setItem('themeMode', mode); } catch {}
    document.documentElement.setAttribute('data-mode', mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  useEffect(() => {
    try { localStorage.setItem('accessibilitySettings', JSON.stringify(accessibility)); } catch {}
    const root = document.documentElement;
    root.setAttribute('data-reduce-motion', String(accessibility.reduceAnimations));
    root.setAttribute('data-font-size', accessibility.fontSize);
    root.setAttribute('data-high-contrast', String(accessibility.highContrast));
  }, [accessibility]);

  const toggleThemeMode = () => setMode((m) => (m === 'light' ? 'dark' : 'light'));
  const updateAccessibility = (s: Partial<AccessibilitySettings>) =>
    setAccessibility((prev) => ({ ...prev, ...s }));

  const theme = useMemo(
    () => createAppTheme(mode, isRtl ? 'rtl' : 'ltr', accessibility.highContrast),
    [mode, isRtl, accessibility.highContrast]
  );

  return (
    <ThemeContext.Provider
      value={{ mode, toggleThemeMode, setMode, accessibility, updateAccessibility, reduceMotion }}
    >
      <CacheProvider value={isRtl ? rtlCache : ltrCache}>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          <MotionConfig reducedMotion={reduceMotion ? 'always' : 'user'}>
            {children}
          </MotionConfig>
        </MuiThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
